import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Save, Eye, EyeOff } from "lucide-react";
import { api, imgUrl, formatApiError } from "../../api";
import { useSettings } from "../../context/SettingsContext";
import {
  HERO_DESKTOP_IMAGE_SPEC,
  HERO_MOBILE_IMAGE_SPEC,
  HERO_SLIDE_DEFAULTS,
  normalizeHeroSlides,
} from "../../data/heroSlides";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const HOMEPAGE_IMAGE_SPECS = {
  story: { width: 1200, height: 1500, aspectRatio: "4 / 5", label: "1200 × 1500 px (4:5)" },
  editorial: { width: 1920, height: 1080, aspectRatio: "16 / 9", label: "1920 × 1080 px (16:9)" },
  styling: { width: 1200, height: 1500, aspectRatio: "4 / 5", label: "1200 × 1500 px (4:5)" },
  deals: { width: 1800, height: 720, aspectRatio: "5 / 2", label: "1800 × 720 px (5:2)" },
  about: { width: 1200, height: 1500, aspectRatio: "4 / 5", label: "1200 × 1500 px (4:5)" },
  instagram: { width: 1080, height: 1350, aspectRatio: "4 / 5", label: "1080 × 1350 px (4:5)" },
};

const inspectImage = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(objectUrl);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("This image could not be read."));
    };
    image.src = objectUrl;
  });

const warnIfImageNeedsCropping = async (file, imageSpec) => {
  if (!imageSpec) return;
  const dimensions = await inspectImage(file);
  const expectedRatio = imageSpec.width / imageSpec.height;
  const actualRatio = dimensions.width / dimensions.height;
  const ratioMismatch = Math.abs(actualRatio - expectedRatio) / expectedRatio > 0.08;
  const belowRecommended = dimensions.width < imageSpec.width || dimensions.height < imageSpec.height;
  if (ratioMismatch || belowRecommended) {
    toast.warning(
      `Uploaded ${dimensions.width}×${dimensions.height}px. For the sharpest crop, use ${imageSpec.label}.`,
    );
  }
};

const normalizeHomepageForm = (content) => ({
  ...(content || {}),
  hero: {
    ...(content?.hero || {}),
    slides: normalizeHeroSlides(content?.hero?.slides),
  },
});

const showApiFailure = (title, error) => {
  toast.error(title, {
    description: formatApiError(error),
    duration: 10000,
    closeButton: true,
  });
};

// Small utility: image upload field
function ImageField({ label, value, onChange, testid, imageSpec, guidance }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      toast.error("Please upload a JPG, PNG or WebP image.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be 8 MB or smaller.");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      await warnIfImageNeedsCropping(file, imageSpec);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded", {
        description: "Use “Save all changes” to publish it on the website.",
      });
    } catch (err) {
      showApiFailure("Image upload failed", err);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="form-block" style={{ marginTop: 12 }}>
      <span className="eyebrow">{label}</span>
      {(imageSpec || guidance) && (
        <p className="hero-image-guidance">
          {imageSpec && <strong>Recommended: {imageSpec.label}.</strong>} {guidance}
        </p>
      )}
      <div className="image-field">
        <div className="image-preview hero-image-preview" style={imageSpec ? { aspectRatio: imageSpec.aspectRatio } : undefined}>
          {value ? <img src={imgUrl(value)} alt={label} /> : <span>No image</span>}
        </div>
        <div className="image-field-actions">
          <label className="button outline-button">
            <input
              ref={ref}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={upload}
              hidden
              data-testid={`${testid}-upload`}
            />
            <Upload size={14} /> {busy ? "Uploading…" : "Upload"}
          </label>
          {value && (
            <button type="button" className="button outline-button" onClick={() => onChange(null)} data-testid={`${testid}-remove`}>
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// A single homepage section editor
function SectionEditor({ title, sectionKey, fields, imageFields, form, setForm, canHide = true }) {
  const section = form[sectionKey] || {};
  const setField = (k, v) => setForm((prev) => ({ ...prev, [sectionKey]: { ...prev[sectionKey], [k]: v } }));
  return (
    <section className="admin-form-section" data-testid={`homepage-section-${sectionKey}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, borderBottom: 0, paddingBottom: 0 }}>{title}</h2>
        {canHide && (
          <label className="toggle" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={section.visible !== false}
              onChange={(e) => setField("visible", e.target.checked)}
              data-testid={`${sectionKey}-visible-toggle`}
            />
            {section.visible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
            {section.visible !== false ? "Visible" : "Hidden"}
          </label>
        )}
      </div>
      <div className="form-grid">
        {fields.map(({ key, label, type = "text", rows, span }) => (
          <label key={key} className={span === 2 ? "span-2" : ""}>
            {label}
            {type === "textarea" ? (
              <textarea
                rows={rows || 3}
                value={section[key] || ""}
                onChange={(e) => setField(key, e.target.value)}
                data-testid={`${sectionKey}-${key}-input`}
              />
            ) : (
              <input
                type={type}
                value={section[key] || ""}
                onChange={(e) => setField(key, e.target.value)}
                data-testid={`${sectionKey}-${key}-input`}
              />
            )}
          </label>
        ))}
      </div>
      {imageFields?.map((f) => (
        <ImageField
          key={f.key}
          label={f.label}
          value={section[f.key]}
          onChange={(v) => setField(f.key, v)}
          testid={`${sectionKey}-${f.key}`}
          imageSpec={f.imageSpec}
          guidance={f.guidance}
        />
      ))}
    </section>
  );
}

export default function AdminHomepage() {
  const { homepage, setHomepage } = useSettings();
  const [form, setForm] = useState(() => normalizeHomepageForm(homepage));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(normalizeHomepageForm(homepage));
  }, [homepage]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/api/admin/homepage", normalizeHomepageForm(form));
      const savedHomepage = normalizeHomepageForm(data);
      setForm(savedHomepage);
      setHomepage(savedHomepage);
      toast.success("Homepage saved", {
        description: "All slider and section changes are now available on the storefront.",
      });
    } catch (err) {
      showApiFailure("Homepage was not saved", err);
    } finally {
      setSaving(false);
    }
  };

  // Instagram images editor
  const insta = form.instagram || { images: [] };
  const addInstaImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      toast.error("Please upload a JPG, PNG or WebP image.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be 8 MB or smaller.");
      e.target.value = "";
      return;
    }
    try {
      await warnIfImageNeedsCropping(file, HOMEPAGE_IMAGE_SPECS.instagram);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/admin/upload", fd);
      setForm((prev) => ({ ...prev, instagram: { ...prev.instagram, images: [...(prev.instagram?.images || []), data.url] } }));
      toast.success("Instagram image added", {
        description: "Use “Save all changes” to publish it on the website.",
      });
    } catch (err) {
      showApiFailure("Instagram image upload failed", err);
    } finally {
      e.target.value = "";
    }
  };
  const removeInstaImage = (i) => {
    setForm((prev) => ({ ...prev, instagram: { ...prev.instagram, images: (prev.instagram?.images || []).filter((_, idx) => idx !== i) } }));
  };

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <span className="eyebrow">Editorial</span>
          <h1>Homepage.</h1>
        </div>
        <button className="button dark-button" onClick={save} disabled={saving} data-testid="homepage-save-button">
          <Save size={14} /> {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>

      {/* ============ 6-SLIDE LUXURY HERO SLIDER EDITOR ============ */}
      <section className="admin-form-section" data-testid="homepage-section-hero-slides">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, borderBottom: 0, paddingBottom: 0 }}>01 · Luxury Hero Slider (6 Editable Slides)</h2>
            <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>
              Full-width campaign imagery with responsive typography. Every slide is fully synced with the storefront.
            </p>
          </div>
          <label className="toggle" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={form.hero?.visible !== false}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  hero: { ...prev.hero, visible: e.target.checked },
                }))
              }
              data-testid="hero-visible-toggle"
            />
            {form.hero?.visible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
            {form.hero?.visible !== false ? "Visible" : "Hidden"}
          </label>
        </div>

        <div className="hero-upload-specs" role="note">
          <div>
            <strong>Desktop / laptop image</strong>
            <span>{HERO_DESKTOP_IMAGE_SPEC.label} · JPG, PNG or WebP · ideally under 1.5 MB</span>
          </div>
          <div>
            <strong>Mobile portrait image</strong>
            <span>{HERO_MOBILE_IMAGE_SPEC.label} · optional, but recommended for the best phone crop</span>
          </div>
          <p>Keep the face and main garment inside the centre safe area. Each uploaded file must be 8 MB or smaller.</p>
        </div>

        {/* 6 Slides */}
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {HERO_SLIDE_DEFAULTS.map((fallbackSlide, idx) => {
            const currentSlides = form.hero?.slides || [];
            const slide = { ...fallbackSlide, ...(currentSlides[idx] || {}) };

            const updateSlideField = (field, val) => {
              setForm((prev) => {
                const updatedSlides = [...(prev.hero?.slides || [])];
                while (updatedSlides.length <= idx) {
                  updatedSlides.push({});
                }
                updatedSlides[idx] = { ...slide, ...updatedSlides[idx], [field]: val };
                return { ...prev, hero: { ...prev.hero, slides: updatedSlides } };
              });
            };

            return (
              <div
                key={idx}
                style={{
                  background: "var(--paper)",
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                  padding: 24,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <span className="eyebrow" style={{ color: "var(--wine)", fontSize: 12 }}>
                    Editorial Slide {idx + 1}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    {idx === 0 ? "Default initial slide" : `Campaign look ${String(idx + 1).padStart(2, "0")}`}
                  </span>
                </div>

                <div className="form-grid">
                  <label>
                    Luxury Tag / Eyebrow
                    <input
                      type="text"
                      value={slide.tag || ""}
                      onChange={(e) => updateSlideField("tag", e.target.value)}
                      placeholder="e.g. HAUTE COUTURE · 2026"
                    />
                  </label>
                  <label>
                    Campaign Title
                    <input
                      type="text"
                      value={slide.title || ""}
                      onChange={(e) => updateSlideField("title", e.target.value)}
                      placeholder="e.g. The Modern Trousseau"
                    />
                  </label>
                  <label className="span-2">
                    Subtitle / Line
                    <input
                      type="text"
                      value={slide.subtitle || ""}
                      onChange={(e) => updateSlideField("subtitle", e.target.value)}
                      placeholder="e.g. Hyderabad Atelier · Bespoke Bridal"
                    />
                  </label>
                  <label className="span-2">
                    Short Description (Small & Luxury)
                    <textarea
                      rows={2}
                      value={slide.description || ""}
                      onChange={(e) => updateSlideField("description", e.target.value)}
                      placeholder="Poetic high-fashion collection description..."
                    />
                  </label>
                  <label>
                    Primary Button Label
                    <input
                      type="text"
                      value={slide.primary_cta_label || ""}
                      onChange={(e) => updateSlideField("primary_cta_label", e.target.value)}
                    />
                  </label>
                  <label>
                    Primary Button Link
                    <input
                      type="text"
                      value={slide.primary_cta_link || ""}
                      onChange={(e) => updateSlideField("primary_cta_link", e.target.value)}
                    />
                  </label>
                  <label>
                    Secondary Button Label
                    <input
                      type="text"
                      value={slide.secondary_cta_label || ""}
                      onChange={(e) => updateSlideField("secondary_cta_label", e.target.value)}
                    />
                  </label>
                  <label>
                    Secondary Button Link
                    <input
                      type="text"
                      value={slide.secondary_cta_link || ""}
                      onChange={(e) => updateSlideField("secondary_cta_link", e.target.value)}
                    />
                  </label>
                </div>

                <ImageField
                  label={`Slide ${idx + 1} · Desktop / Main Visual`}
                  value={slide.image}
                  onChange={(url) => updateSlideField("image", url)}
                  testid={`hero-slide-${idx}-image`}
                  imageSpec={HERO_DESKTOP_IMAGE_SPEC}
                  guidance="This image fills the hero on desktop and laptop screens."
                />
                <ImageField
                  label={`Slide ${idx + 1} · Mobile Portrait Visual (Optional)`}
                  value={slide.mobile_image}
                  onChange={(url) => updateSlideField("mobile_image", url)}
                  testid={`hero-slide-${idx}-mobile-image`}
                  imageSpec={HERO_MOBILE_IMAGE_SPEC}
                  guidance="Phones select this portrait image automatically. If empty, the desktop image is centre-cropped."
                />
              </div>
            );
          })}
        </div>
      </section>

      <SectionEditor title="Homepage product shelf" sectionKey="product_shelf" form={form} setForm={setForm} fields={[
        { key: "eyebrow", label: "Eyebrow" }, { key: "heading", label: "Heading" },
        { key: "view_all_label", label: "View-all label" }, { key: "view_all_link", label: "View-all link" },
        { key: "empty_message", label: "Empty-category message", span: 2 },
      ]} />

      <SectionEditor title="Atelier assurance" sectionKey="assurance" form={form} setForm={setForm} fields={[
        { key: "eyebrow", label: "Eyebrow" }, { key: "heading", label: "Heading" },
        { key: "atelier_title", label: "Atelier title" }, { key: "atelier_body", label: "Atelier text", type: "textarea" },
        { key: "atelier_link", label: "Atelier link" },
        { key: "sizing_title", label: "Sizing title" }, { key: "sizing_body", label: "Sizing text", type: "textarea" },
        { key: "sizing_link", label: "Sizing link" },
        { key: "dispatch_title", label: "Dispatch title" }, { key: "dispatch_body", label: "Dispatch text", type: "textarea" },
        { key: "dispatch_link", label: "Dispatch link" },
        { key: "care_title", label: "Care title" }, { key: "care_body", label: "Care text", type: "textarea" },
        { key: "whatsapp_message", label: "Care WhatsApp message", type: "textarea", span: 2 },
      ]} />

      <SectionEditor title="House manifesto" sectionKey="manifesto" form={form} setForm={setForm} fields={[
        { key: "eyebrow", label: "Eyebrow" }, { key: "heading", label: "Heading" },
        { key: "body", label: "Body copy", type: "textarea", span: 2, rows: 4 },
        { key: "note", label: "Closing note", type: "textarea", span: 2 },
        { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
      ]} />

      <SectionEditor
        title="Brand story"
        sectionKey="brand_story"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "body", label: "Body copy", type: "textarea", span: 2, rows: 4 },
          { key: "cta_label", label: "CTA label" },
          { key: "cta_link", label: "CTA link" },
          { key: "image_caption", label: "Image caption" },
          { key: "principle1", label: "Principle 1" },
          { key: "principle2", label: "Principle 2" },
          { key: "principle3", label: "Principle 3" },
        ]}
        imageFields={[{
          key: "image",
          label: "Story image",
          imageSpec: HOMEPAGE_IMAGE_SPECS.story,
          guidance: "Portrait imagery keeps the editorial layout balanced on desktop and mobile.",
        }]}
      />

      <SectionEditor
        title="Shop by category"
        sectionKey="categories"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "view_all_label", label: "View-all label" },
          { key: "view_all_link", label: "View-all link" },
          { key: "card_eyebrow", label: "Category-card eyebrow" },
        ]}
      />

      <SectionEditor
        title="Top rated (first 4 products with 'Top rated' flag)"
        sectionKey="top_rated"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "description", label: "Description", type: "textarea", span: 2 },
          { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
        ]}
      />

      <SectionEditor
        title="Featured (products with 'Featured' flag)"
        sectionKey="featured"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "description", label: "Description", type: "textarea", span: 2 },
          { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
        ]}
      />

      <SectionEditor title="House codes" sectionKey="house_codes" form={form} setForm={setForm} fields={[
        { key: "eyebrow", label: "Eyebrow" }, { key: "heading", label: "Heading" },
        { key: "item1_title", label: "Principle 1 title" }, { key: "item1_body", label: "Principle 1 text", type: "textarea" },
        { key: "item2_title", label: "Principle 2 title" }, { key: "item2_body", label: "Principle 2 text", type: "textarea" },
        { key: "item3_title", label: "Principle 3 title" }, { key: "item3_body", label: "Principle 3 text", type: "textarea" },
      ]} />

      <SectionEditor title="Editorial banner" sectionKey="editorial_banner" form={form} setForm={setForm} fields={[
        { key: "edition_left", label: "Edition label (left)" }, { key: "edition_right", label: "Edition label (right)" },
        { key: "eyebrow", label: "Eyebrow" }, { key: "title_line1", label: "Title line 1" },
        { key: "title_line2", label: "Title line 2" }, { key: "body", label: "Body copy", type: "textarea", span: 2, rows: 3 },
        { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
      ]} imageFields={[{
        key: "image",
        label: "Editorial background image",
        imageSpec: HOMEPAGE_IMAGE_SPECS.editorial,
        guidance: "Keep important details near the centre so responsive crops remain elegant.",
      }]} />

      <SectionEditor
        title="New arrivals (products with 'New arrival' flag)"
        sectionKey="new_arrivals"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "description", label: "Description", type: "textarea", span: 2 },
          { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
        ]}
      />

      <SectionEditor
        title="Personal discovery / recommended products"
        sectionKey="discovery"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "generic_message", label: "First-visit message", type: "textarea", span: 2 },
          { key: "personalized_message", label: "Personalized message (keep {category} where the category name should appear)", type: "textarea", span: 2 },
          { key: "refresh_note", label: "Refresh note" },
          { key: "swipe_label", label: "Mobile swipe label" },
        ]}
      />

      <SectionEditor title="Private styling" sectionKey="private_styling" form={form} setForm={setForm} fields={[
        { key: "eyebrow", label: "Eyebrow" }, { key: "heading", label: "Heading" },
        { key: "body", label: "Body copy", type: "textarea", span: 2, rows: 4 },
        { key: "point1", label: "Step 1" }, { key: "point2", label: "Step 2" }, { key: "point3", label: "Step 3" },
        { key: "primary_cta_label", label: "Primary CTA label" }, { key: "primary_cta_link", label: "Primary CTA link" },
        { key: "secondary_cta_label", label: "WhatsApp CTA label" },
        { key: "whatsapp_message", label: "WhatsApp prefilled message", type: "textarea", span: 2 },
      ]} imageFields={[{
        key: "image",
        label: "Styling image",
        imageSpec: HOMEPAGE_IMAGE_SPECS.styling,
        guidance: "Portrait imagery is used for the styling feature on every screen size.",
      }]} />

      <SectionEditor
        title="Old deals / offers (products with 'Old deal' flag)"
        sectionKey="old_deals"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "description", label: "Description", type: "textarea", span: 2 },
          { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
        ]}
        imageFields={[{
          key: "banner_image",
          label: "Deals banner (optional)",
          imageSpec: HOMEPAGE_IMAGE_SPECS.deals,
          guidance: "Use a centre-safe composition; phones display a closer responsive crop.",
        }]}
      />

      <SectionEditor
        title="Launch offer / first-order strip"
        sectionKey="launch_offer"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "body", label: "Body copy", type: "textarea", span: 2 },
          { key: "cta_label", label: "CTA label" },
          { key: "whatsapp_message", label: "WhatsApp prefilled message", type: "textarea", span: 2 },
        ]}
      />

      <section className="admin-form-section" data-testid="homepage-section-instagram">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, borderBottom: 0, paddingBottom: 0 }}>Instagram grid</h2>
          <label className="toggle">
            <input
              type="checkbox"
              checked={insta.visible !== false}
              onChange={(e) => setForm({ ...form, instagram: { ...insta, visible: e.target.checked } })}
              data-testid="instagram-visible-toggle"
            />
            {insta.visible !== false ? "Visible" : "Hidden"}
          </label>
        </div>
        <div className="form-grid">
          <label>
            Eyebrow
            <input value={insta.eyebrow || ""} onChange={(e) => setForm({ ...form, instagram: { ...insta, eyebrow: e.target.value } })} data-testid="instagram-eyebrow-input" />
          </label>
          <label>
            Heading
            <input value={insta.heading || ""} onChange={(e) => setForm({ ...form, instagram: { ...insta, heading: e.target.value } })} data-testid="instagram-heading-input" />
          </label>
          <label className="span-2">
            Body
            <textarea rows={2} value={insta.body || ""} onChange={(e) => setForm({ ...form, instagram: { ...insta, body: e.target.value } })} data-testid="instagram-body-input" />
          </label>
          <label>
            CTA label
            <input value={insta.cta_label || ""} onChange={(e) => setForm({ ...form, instagram: { ...insta, cta_label: e.target.value } })} />
          </label>
        </div>
        <div className="form-block">
          <span className="eyebrow">Grid images</span>
          <p className="hero-image-guidance">
            <strong>Recommended: {HOMEPAGE_IMAGE_SPECS.instagram.label}.</strong> Portrait images create the cleanest Instagram grid.
          </p>
          <div className="image-grid">
            {(insta.images || []).map((img, i) => (
              <div className="image-tile" key={img + i}>
                <img src={imgUrl(img)} alt={`insta ${i + 1}`} />
                <div className="image-tile-actions">
                  <button type="button" onClick={() => removeInstaImage(i)} title="Remove" data-testid={`instagram-remove-${i}`}>
                    ×
                  </button>
                </div>
              </div>
            ))}
            <label className="image-upload">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={addInstaImage}
                data-testid="instagram-add-image"
              />
              <Upload size={18} />
              <span>Add image</span>
            </label>
          </div>
        </div>
      </section>

      <SectionEditor
        title="Newsletter"
        sectionKey="newsletter"
        form={form}
        setForm={setForm}
        fields={[
          { key: "eyebrow", label: "Eyebrow" },
          { key: "heading", label: "Heading" },
          { key: "button_label", label: "Button text" },
          { key: "body", label: "Body copy", type: "textarea", span: 2 },
          { key: "note", label: "Privacy note", span: 2 },
        ]}
      />

      <SectionEditor
        title="Footer content"
        sectionKey="footer"
        form={form}
        setForm={setForm}
        canHide={false}
        fields={[
          { key: "footer_note", label: "Footer note" },
          { key: "wordmark_line1", label: "Wordmark — line 1" },
          { key: "wordmark_line2", label: "Wordmark — line 2" },
          { key: "address", label: "Store address (used by the footer map)", span: 2 },
        ]}
      />

      <SectionEditor
        title="About page"
        sectionKey="about"
        form={form}
        setForm={setForm}
        canHide={false}
        fields={[
          { key: "heading", label: "Heading" },
          { key: "body", label: "Body copy", type: "textarea", span: 2, rows: 6 },
        ]}
        imageFields={[{
          key: "image",
          label: "About image",
          imageSpec: HOMEPAGE_IMAGE_SPECS.about,
          guidance: "Portrait imagery keeps the About page composed on desktop and mobile.",
        }]}
      />

      <SectionEditor
        title="Customization page"
        sectionKey="customization"
        form={form}
        setForm={setForm}
        canHide={false}
        fields={[
          { key: "heading", label: "Heading" },
          { key: "body", label: "Body copy", type: "textarea", span: 2, rows: 5 },
        ]}
      />

      <div className="admin-form-actions">
        <button className="button dark-button" onClick={save} disabled={saving} data-testid="homepage-save-button-bottom">
          <Save size={14} /> {saving ? "Saving…" : "Save all homepage changes"}
        </button>
      </div>
    </div>
  );
}
